// src/pages/emailMarketing/CampaignCreatePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import emailMarketingService from '../../helpers/emailMarketingService';
import SegmentSelector from '../../components/emailMarketing/SegmentSelector';
import EmailPreview from '../../components/emailMarketing/EmailPreview';
import EmailEditor from '../../components/emailMarketing/EmailEditor';
import Swal from 'sweetalert2';

function CampaignCreatePage() {
    const { id } = useParams();
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const isEdit = !!id;
    
    const [marcas, setMarcas] = useState([]);
    const [selectedMarca, setSelectedMarca] = useState(null);
    const [selectedMarcaData, setSelectedMarcaData] = useState(null); // Datos completos de la marca seleccionada
    const [loading, setLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        subject: '',
        htmlBody: '',
        textBody: '',
        scheduledAt: '',
        segmentFilters: {}
    });
    
    const [recipientsCount, setRecipientsCount] = useState(0);
    const [calculatingRecipients, setCalculatingRecipients] = useState(false);
    const [isSaved, setIsSaved] = useState(false); // Para saber si la campaña ya fue guardada
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [availableClients, setAvailableClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientSearchTerm, setClientSearchTerm] = useState('');

    useEffect(() => {
        fetchMarcas();
        if (isEdit) {
            fetchCampaign();
        }
    }, [id, user]);

    useEffect(() => {
        if (selectedMarca) {
            fetchMarcaData();
            fetchClients();
        }
    }, [selectedMarca]);

    useEffect(() => {
        // Si hay clientes seleccionados, usar ese número
        if (selectedClientIds.length > 0) {
            setRecipientsCount(selectedClientIds.length);
            return;
        }
        
        // Solo calcular automáticamente si no está guardada inicialmente
        // Si está guardada, el valor viene del backend y no necesitamos recalcular hasta que cambien los filtros
        if (!isEdit || !isSaved) {
            if (selectedMarca && formData.segmentFilters && Object.keys(formData.segmentFilters).length > 0) {
                calculateRecipientsCount(formData.segmentFilters);
            } else if (selectedMarca && (!formData.segmentFilters || Object.keys(formData.segmentFilters).length === 0)) {
                // Si no hay segmentación, contar todos los contactos activos
                calculateRecipientsCount({});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMarca, formData.segmentFilters, selectedClientIds]);

    const fetchMarcas = async () => {
        try {
            if (user?.userId) {
                const response = await get(`marcas/user/${user.userId}`);
                setMarcas(response);
                if (response.length > 0) {
                    setSelectedMarca(response[0]._id);
                }
            }
        } catch (error) {
            // Error silencioso
        }
    };

    const fetchMarcaData = async () => {
        if (!selectedMarca) return;
        try {
            const marcaData = await get(`marcas/${selectedMarca}`);
            setSelectedMarcaData(marcaData);
        } catch (error) {
            // Error silencioso
        }
    };

    const fetchClients = async () => {
        if (!selectedMarca) return;
        
        setLoadingClients(true);
        try {
            // Obtener clientes de la marca usando el endpoint de email marketing
            const response = await emailMarketingService.getContacts(selectedMarca, {
                acceptsMarketing: true,
                limit: 1000 // Obtener hasta 1000 clientes para selección
            });
            
            const clients = response.data || response || [];
            setAvailableClients(clients);
        } catch (error) {
            Swal.fire('Error', 'No se pudieron cargar los clientes', 'error');
        } finally {
            setLoadingClients(false);
        }
    };

    const fetchCampaign = async () => {
        try {
            const campaign = await emailMarketingService.getCampaign(id);
            setFormData({
                name: campaign.name || '',
                subject: campaign.subject || '',
                htmlBody: campaign.htmlBody || '',
                textBody: campaign.textBody || '',
                scheduledAt: campaign.scheduledAt ? new Date(campaign.scheduledAt).toISOString().slice(0, 16) : '',
                segmentFilters: campaign.segmentFilters || {}
            });
            setSelectedMarca(campaign.marcaId);
            
            // Cargar clientes seleccionados si existen
            if (campaign.selectedClientIds && Array.isArray(campaign.selectedClientIds)) {
                setSelectedClientIds(campaign.selectedClientIds);
            }
            
            // Usar el totalRecipients del backend si existe (es más preciso que calcular manualmente)
            if (campaign.totalRecipients !== undefined && campaign.totalRecipients !== null) {
                setRecipientsCount(campaign.totalRecipients);
                setIsSaved(true); // Ya está guardada, usar valor del backend
            } else if (campaign.segmentFilters && Object.keys(campaign.segmentFilters).length > 0) {
                // Si no hay totalRecipients, calcular como estimación
                calculateRecipientsCount(campaign.segmentFilters);
                setIsSaved(false);
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
            navigate('/email-marketing/campaigns');
        }
    };

    const calculateRecipientsCount = async (segmentFilters = {}) => {
        if (!selectedMarca) return;
        
        setCalculatingRecipients(true);
        try {
            // Usar el método del servicio que calcula usando la misma lógica que el backend
            const count = await emailMarketingService.calculateRecipientsBeforeCreate(selectedMarca, segmentFilters);
            setRecipientsCount(count);
        } catch (error) {
            setRecipientsCount(0);
        } finally {
            setCalculatingRecipients(false);
        }
    };

    const validateForm = () => {
        // Validar que tenga unsubscribe.url
        if (!formData.htmlBody.includes('{{unsubscribe.url}}') && !formData.htmlBody.includes('{{unsubscribe.link}}')) {
            Swal.fire({
                title: 'Validación requerida',
                html: `
                    <p>El contenido HTML debe incluir el enlace de baja para cumplir con GDPR.</p>
                    <p class="text-sm mt-2">Añade: <code>{{unsubscribe.url}}</code> o <code>{{unsubscribe.link}}</code></p>
                `,
                icon: 'warning',
                confirmButtonText: 'Entendido'
            });
            return false;
        }
        
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Verificar si el envío está bloqueado
        if (selectedMarcaData?.emailSendingBlocked) {
            const reason = selectedMarcaData.emailSendingBlockedReason || 'tasa de quejas alta';
            Swal.fire({
                title: 'Envío bloqueado',
                html: `
                    <p>El envío de emails está bloqueado para esta marca.</p>
                    <p class="text-sm mt-2"><strong>Razón:</strong> ${reason}</p>
                    ${selectedMarcaData.emailSendingBlockedAt ? 
                        `<p class="text-xs mt-1 text-gray-600">Bloqueado desde: ${new Date(selectedMarcaData.emailSendingBlockedAt).toLocaleDateString()}</p>` 
                        : ''}
                    <p class="text-sm mt-3">Contacta con soporte para más información.</p>
                `,
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
            return;
        }
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);

        try {
            // Limpiar segmentFilters para enviar solo propiedades con valores
            let cleanedSegmentFilters = undefined;
            if (formData.segmentFilters && Object.keys(formData.segmentFilters).length > 0) {
                cleanedSegmentFilters = {};
                
                // Solo incluir propiedades que tengan valores
                if (formData.segmentFilters.rfmSegments?.length > 0) {
                    cleanedSegmentFilters.rfmSegments = formData.segmentFilters.rfmSegments;
                }
                if (formData.segmentFilters.productSegments?.length > 0) {
                    cleanedSegmentFilters.productSegments = formData.segmentFilters.productSegments;
                }
                if (formData.segmentFilters.behavioralSegments?.length > 0) {
                    cleanedSegmentFilters.behavioralSegments = formData.segmentFilters.behavioralSegments;
                }
                if (formData.segmentFilters.tags?.length > 0) {
                    cleanedSegmentFilters.tags = formData.segmentFilters.tags;
                }
                if (formData.segmentFilters.country) {
                    cleanedSegmentFilters.country = formData.segmentFilters.country;
                }
                if (formData.segmentFilters.city) {
                    cleanedSegmentFilters.city = formData.segmentFilters.city;
                }
                if (formData.segmentFilters.lastActiveDays) {
                    cleanedSegmentFilters.lastActiveDays = formData.segmentFilters.lastActiveDays;
                }
                
                // Solo enviar si tiene al menos una propiedad con valor
                if (Object.keys(cleanedSegmentFilters).length === 0) {
                    cleanedSegmentFilters = undefined;
                }
            }

            const campaignData = {
                name: formData.name,
                subject: formData.subject,
                htmlBody: formData.htmlBody,
                textBody: formData.textBody,
                scheduledAt: formData.scheduledAt || undefined,
                segmentFilters: cleanedSegmentFilters,
                selectedClientIds: selectedClientIds.length > 0 ? selectedClientIds : undefined
            };

            let savedCampaign;
            if (isEdit) {
                savedCampaign = await emailMarketingService.updateCampaign(id, campaignData);
            } else {
                savedCampaign = await emailMarketingService.createCampaign(selectedMarca, campaignData);
            }
            
            // Actualizar el contador con el valor del backend (es la fuente de verdad)
            if (savedCampaign?.totalRecipients !== undefined && savedCampaign?.totalRecipients !== null) {
                setRecipientsCount(savedCampaign.totalRecipients);
                setIsSaved(true); // Marcar como guardada para mostrar el valor real
            } else {
                // Si el backend no devuelve totalRecipients, calcularlo
                const campaignId = savedCampaign?._id || id;
                if (campaignId && cleanedSegmentFilters) {
                    try {
                        const recipientsData = await emailMarketingService.calculateRecipients(campaignId);
                        if (recipientsData?.count !== undefined) {
                            setRecipientsCount(recipientsData.count);
                        }
                    } catch (calcError) {
                        // Error silencioso, no bloqueamos el guardado
                    }
                }
            }
            
            Swal.fire('Éxito', isEdit ? 'Campaña actualizada correctamente' : 'Campaña creada correctamente', 'success');
            navigate('/email-marketing/campaigns');
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <h1 className="text-3xl font-bold mb-6">
                {isEdit ? 'Editar Campaña' : 'Nueva Campaña'}
            </h1>

            <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow">
                {/* Advertencia de bloqueo de envío */}
                {selectedMarcaData?.emailSendingBlocked && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-sm font-medium text-red-800">
                                    Envío de emails bloqueado
                                </h3>
                                <div className="mt-2 text-sm text-red-700">
                                    <p>
                                        El envío de emails está bloqueado para esta marca.
                                        <strong> Razón:</strong> {selectedMarcaData.emailSendingBlockedReason || 'Tasa de quejas alta'}
                                    </p>
                                    {selectedMarcaData.emailSendingBlockedAt && (
                                        <p className="mt-1 text-xs">
                                            Bloqueado desde: {new Date(selectedMarcaData.emailSendingBlockedAt).toLocaleDateString()}
                                        </p>
                                    )}
                                    <p className="mt-2">
                                        Puedes crear y guardar campañas, pero no se podrán enviar hasta que se resuelva el bloqueo. 
                                        Contacta con soporte para más información.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Marca
                    </label>
                    <select
                        value={selectedMarca || ''}
                        onChange={(e) => setSelectedMarca(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                        required
                    >
                        <option value="">Selecciona una marca</option>
                        {marcas.map(marca => (
                            <option key={marca._id} value={marca._id}>
                                {marca.nombre}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Campaña *
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Asunto del Email *
                    </label>
                    <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                        required
                    />
                </div>

                <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Contenido del Email *
                        </label>
                        <EmailPreview 
                            htmlBody={formData.htmlBody} 
                            subject={formData.subject}
                            marcaName={marcas.find(m => m._id === selectedMarca)?.nombre || 'Mi Marca'}
                        />
                    </div>
                    
                    {/* Editor WYSIWYG */}
                    <EmailEditor
                        value={formData.htmlBody}
                        onChange={(html) => setFormData({ ...formData, htmlBody: html })}
                        placeholder="Escribe el contenido del email aquí. Usa los botones de placeholders para insertar variables dinámicas."
                    />
                    
                    <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-500">
                            <strong>Placeholders disponibles:</strong>
                        </p>
                        <div className="text-xs text-gray-600 space-y-1">
                            <p>• <code>{'{{contact.name}}'}</code> - Nombre del contacto</p>
                            <p>• <code>{'{{contact.email}}'}</code> - Email del contacto</p>
                            <p>• <code>{'{{contact.firstName}}'}</code> - Primer nombre</p>
                            <p>• <code>{'{{contact.lastName}}'}</code> - Apellido</p>
                            <p>• <code>{'{{marca.name}}'}</code> - Nombre de la marca</p>
                            <p>• <code>{'{{marca.email}}'}</code> - Email de la marca</p>
                            <p>• <code>{'{{marca.phone}}'}</code> - Teléfono de la marca</p>
                            <p>• <code>{'{{unsubscribe.url}}'}</code> - <strong>Requerido</strong> - URL completa de baja</p>
                            <p>• <code>{'{{unsubscribe.link}}'}</code> - Enlace de baja (sin protocolo)</p>
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contenido Texto Plano (Opcional)
                    </label>
                    <textarea
                        value={formData.textBody}
                        onChange={(e) => setFormData({ ...formData, textBody: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                        rows={5}
                        placeholder="Versión en texto plano del email"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha Programada (Opcional)
                    </label>
                    <input
                        type="datetime-local"
                        value={formData.scheduledAt}
                        onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                    />
                </div>

                {/* Selector de Segmentación */}
                {selectedMarca && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-lg font-semibold mb-4">Segmentación de Destinatarios</h3>
                        <SegmentSelector
                            marcaId={selectedMarca}
                            value={formData.segmentFilters}
                            onChange={(filters) => {
                                setFormData({ ...formData, segmentFilters: filters });
                                // Si cambian los filtros después de guardar, marcar como no guardado
                                if (isSaved) {
                                    setIsSaved(false);
                                }
                            }}
                            onRecipientsCountChange={(count) => {
                                // Solo actualizar si no está guardada (para evitar sobrescribir el valor del backend)
                                if (!isSaved) {
                                    setRecipientsCount(count);
                                }
                            }}
                        />
                        <div className="mt-4 p-3 bg-blue-50 rounded">
                            <p className="text-sm">
                                <strong>Destinatarios:</strong>{' '}
                                {calculatingRecipients ? (
                                    <span className="text-gray-500">Calculando...</span>
                                ) : (
                                    <span className="text-blue-700 font-bold">{recipientsCount}</span>
                                )}
                            </p>
                            {recipientsCount === 0 && !calculatingRecipients && (
                                <p className="text-xs text-gray-600 mt-1">
                                    No hay contactos que cumplan los criterios de segmentación seleccionados.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Selector de Clientes Específicos */}
                {selectedMarca && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-lg font-semibold mb-4">Seleccionar Clientes Específicos (Opcional)</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Puedes seleccionar clientes específicos de tu marca para enviarles la campaña. 
                            Si seleccionas clientes, se enviará solo a ellos (ignorando la segmentación).
                        </p>
                        
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Buscar cliente por nombre o email..."
                                value={clientSearchTerm}
                                onChange={(e) => setClientSearchTerm(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded"
                            />
                        </div>

                        {loadingClients ? (
                            <div className="text-gray-500">Cargando clientes...</div>
                        ) : (
                            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded p-2">
                                {availableClients
                                    .filter(client => {
                                        if (!clientSearchTerm) return true;
                                        const search = clientSearchTerm.toLowerCase();
                                        return (
                                            (client.name && client.name.toLowerCase().includes(search)) ||
                                            (client.email && client.email.toLowerCase().includes(search))
                                        );
                                    })
                                    .map(client => (
                                        <label
                                            key={client._id}
                                            className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedClientIds.includes(client._id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedClientIds([...selectedClientIds, client._id]);
                                                    } else {
                                                        setSelectedClientIds(selectedClientIds.filter(id => id !== client._id));
                                                    }
                                                    setIsSaved(false);
                                                }}
                                                className="mr-2"
                                            />
                                            <div className="flex-1">
                                                <div className="text-sm font-medium">
                                                    {client.name || 'Sin nombre'}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {client.email}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                {availableClients.filter(client => {
                                    if (!clientSearchTerm) return true;
                                    const search = clientSearchTerm.toLowerCase();
                                    return (
                                        (client.name && client.name.toLowerCase().includes(search)) ||
                                        (client.email && client.email.toLowerCase().includes(search))
                                    );
                                }).length === 0 && (
                                    <div className="text-gray-500 text-center py-4">
                                        No se encontraron clientes
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedClientIds.length > 0 && (
                            <div className="mt-4 p-3 bg-blue-50 rounded">
                                <p className="text-sm">
                                    <strong>Clientes seleccionados:</strong>{' '}
                                    <span className="text-blue-700 font-bold">{selectedClientIds.length}</span>
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                    La campaña se enviará solo a estos clientes seleccionados.
                                </p>
                            </div>
                        )}
                    </div>
                )}


                <div className="flex space-x-4">
                    <button
                        type="submit"
                        disabled={loading || selectedMarcaData?.emailSendingBlocked}
                        className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={selectedMarcaData?.emailSendingBlocked ? 'No se puede guardar: envío bloqueado' : ''}
                    >
                        {loading ? 'Guardando...' : 'Guardar Draft'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/email-marketing/campaigns')}
                        className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
                    >
                        Cancelar
                    </button>
                </div>
            </form>
        </div>
    );
}

export default CampaignCreatePage;

